import { Form, Input, Key, Modal, Select, ShowInput, Tag, TreeSelect } from '@maxtropy/components';
import { Col, Spin } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { DefaultOptionType } from 'rc-tree-select/lib/TreeSelect';
import { useEffect, useMemo, useState } from 'react';
import {
  apiV2StaffGetAllStaffSimplePost,
  apiV2WorkTeamAddPost,
  apiV2WorkTeamInfoPost,
  apiV2WorkTeamUpdatePost,
  V2WorkTeamInfoPostResponse,
  V2WorkTeamUpdatePostRequest,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

const formItemLayout = {
  labelCol: { span: 4 },
};

interface Props {
  modalOpen: boolean;
  setModalOpen: (v: boolean) => void;
  treeData: DefaultOptionType[];
  isEdit?: boolean;
  teamId?: Key;
  isDetail?: boolean;
  updateFn?: () => void;
}

export type Worker = Exclude<V2WorkTeamInfoPostResponse['workers'], undefined>[number];
type WorkerOptionPayload = Pick<Worker, 'id' | 'name' | 'no'>;

export interface CreateTeamGroupParams extends V2WorkTeamUpdatePostRequest {
  workTeamId?: string | number;
  leadWorkerIds?: number[];
  workerIds?: number[];
}

const CreateTeamModal: React.FC<Props> = ({
  modalOpen,
  setModalOpen,
  treeData,
  isEdit = false,
  teamId,
  isDetail = false,
  updateFn,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [workerOptions, setWorkerOptions] = useState<{ label: string; value: number; worker: any }[]>([]);
  const [detailLeadWorkers, setDetailLeadWorkers] = useState<WorkerOptionPayload[]>([]);
  const [detailWorkers, setDetailWorkers] = useState<WorkerOptionPayload[]>([]);

  const selectedLeadWorkerIds = (Form.useWatch('leadWorkerIds', form) ?? []) as number[];
  const selectedWorkerIds = (Form.useWatch('workerIds', form) ?? []) as number[];

  const leadWorkerSelectOptions = useMemo(
    () =>
      workerOptions.map(o => ({
        ...o,
        disabled: selectedWorkerIds.includes(o.value) && !selectedLeadWorkerIds.includes(o.value),
      })),
    [workerOptions, selectedWorkerIds, selectedLeadWorkerIds]
  );

  const workerSelectOptions = useMemo(
    () =>
      workerOptions.map(o => ({
        ...o,
        disabled: selectedLeadWorkerIds.includes(o.value) && !selectedWorkerIds.includes(o.value),
      })),
    [workerOptions, selectedLeadWorkerIds, selectedWorkerIds]
  );

  const workerNameMap = useMemo(() => {
    const map = new Map<number, string>();
    workerOptions.forEach(item => {
      if (item.worker.name) map.set(Number(item.value), item.worker.name);
    });
    detailLeadWorkers.forEach(item => {
      if (item.name) map.set(Number(item.id), item.name);
    });
    detailWorkers.forEach(item => {
      if (item.name) map.set(Number(item.id), item.name);
    });
    return map;
  }, [workerOptions, detailLeadWorkers, detailWorkers]);

  const getWorkerDisplayName = (id?: number) => {
    if (id === undefined || id === null || Number.isNaN(id)) return '';
    return workerNameMap.get(Number(id)) || `ID:${id}`;
  };

  const buildWorkerPayload = (ids: number[], fallbackWorkers: WorkerOptionPayload[]) => {
    const optionWorkerMap = new Map(workerOptions.map(item => [Number(item.value), item.worker] as const));
    const fallbackWorkerMap = new Map(
      fallbackWorkers.map(
        item =>
          [
            Number(item.id),
            {
              id: Number(item.id),
              name: item.name,
              no: item.no,
            },
          ] as const
      )
    );

    return ids.map(id => optionWorkerMap.get(id) ?? fallbackWorkerMap.get(id)).filter(Boolean) as WorkerOptionPayload[];
  };

  // Fetch all available staff for dropdown options
  useEffect(() => {
    apiV2StaffGetAllStaffSimplePost().then(res => {
      const options = (res.list ?? []).map(w => {
        return {
          label: w.staffCode ? `${w.name}（${w.staffCode}）` : `${w.name}`,
          value: w.id!,
          worker: {
            id: w.id,
            name: w.name,
            no: w.staffCode,
          },
        };
      });
      setWorkerOptions(options);
    });
  }, []);

  useEffect(() => {
    if (teamId) {
      setLoading(true);
      apiV2WorkTeamInfoPost({ id: teamId })
        .then(res => {
          const leadWorkers = (res.leadWorkers ?? []).map(i => {
            return { id: i.id, name: i.name, no: i.no };
          });

          const workers = (res.workers ?? []).map(i => {
            return { id: i.id, name: i.name, no: i.no };
          });

          form.setFieldsValue({
            name: res.name,
            mcids: (res.customers ?? []).map(i => i.mcid),
            leadWorkerIds: leadWorkers.map(i => i.id),
            workerIds: workers.map(i => i.id),
            mcidNames: (res.customers ?? []).map(i => i.name).join('、'),
          });
          setDetailLeadWorkers(leadWorkers);
          setDetailWorkers(workers);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const onCancel = () => {
    setModalOpen(false);
  };

  const onCloseSelectedTag = (fieldName: 'leadWorkerIds' | 'workerIds', removedId: number) => {
    const currentValues = (form.getFieldValue(fieldName) ?? []) as number[];
    form.setFieldsValue({
      [fieldName]: currentValues.filter(id => id !== removedId),
    });
  };

  const onLeadWorkerChange = (ids: Array<number>) => {
    const workerIdSet = new Set(selectedWorkerIds);
    form.setFieldsValue({
      leadWorkerIds: (ids ?? []).filter(id => !workerIdSet.has(id)),
    });
  };

  const onWorkerChange = (ids: Array<number>) => {
    const leadWorkerIdSet = new Set(selectedLeadWorkerIds);
    form.setFieldsValue({
      workerIds: (ids ?? []).filter(id => !leadWorkerIdSet.has(id)),
    });
  };

  const renderWorkerTag = (props: CustomTagProps) => {
    const id = Number(props.value);
    const labelText = typeof props.label === 'string' || typeof props.label === 'number' ? `${props.label}` : '';
    const fallbackLabel = Number.isFinite(id) ? getWorkerDisplayName(id) : `${props.value ?? ''}`;
    const text = labelText || fallbackLabel;
    return (
      <Tag closable={props.closable} onClose={props.onClose}>
        {text}
      </Tag>
    );
  };

  const onFinish = (v: CreateTeamGroupParams) => {
    const leadWorkerIds = v.leadWorkerIds ?? [];
    const workerIds = v.workerIds ?? [];
    const leadWorkersPayload = buildWorkerPayload(leadWorkerIds, detailLeadWorkers);
    const workersPayload = buildWorkerPayload(workerIds, detailWorkers);

    if (isEdit && teamId) {
      apiV2WorkTeamUpdatePost({
        workTeamId: teamId,
        name: v.name,
        mcids: v.mcids,
        leadWorkers: leadWorkersPayload,
        workers: workersPayload,
      }).then(() => {
        setModalOpen(false);
        updateFn?.();
      });
      return;
    }

    apiV2WorkTeamAddPost({
      name: v.name,
      mcids: form.getFieldValue('mcids')?.map((i: DefaultOptionType) => i.value),
      leadWorkers: leadWorkersPayload,
      workers: workersPayload,
    }).then(() => {
      setModalOpen(false);
      updateFn?.();
    });
  };

  return (
    <Modal
      title={(isDetail ? '查看' : isEdit ? '编辑' : '新建') + '班组'}
      size="large"
      open={modalOpen}
      onCancel={onCancel}
      onOk={() => {
        if (isDetail) {
          setModalOpen(false);
          return;
        }
        form.submit();
      }}
      contentClassName="modal-form-content"
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          {...formItemLayout}
          labelAlign="left"
          className={styles.formItemX}
          onFinish={(v: CreateTeamGroupParams) => {
            onFinish(v);
          }}
        >
          <Col span={22}>
            {isDetail ? (
              <Form.Item label="班组名称" name="name">
                <ShowInput />
              </Form.Item>
            ) : (
              <Form.Item
                label="班组名称"
                name="name"
                rules={[
                  { required: true, message: '请输入班组名称', whitespace: true, type: 'string' },
                  { max: 15, message: '最多输入15个字符' },
                  { pattern: /^((?![\\/:*?<>|.]).)+$/, message: '请确保班组名称中不含下列字符：\\ / : * ? < > | .' },
                ]}
              >
                <Input placeholder="请输入" />
              </Form.Item>
            )}
          </Col>
          <Col span={22}>
            {isDetail ? (
              <Form.Item name="mcidNames" label="所属组织">
                <ShowInput />
              </Form.Item>
            ) : (
              <Form.Item name="mcids" label="所属组织" rules={[{ required: true, message: '请选择所属组织' }]}>
                <TreeSelect
                  showCheckedStrategy={TreeSelect.SHOW_ALL}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  treeDefaultExpandAll
                  treeData={treeData}
                  placeholder="请选择"
                  style={{ width: '100%' }}
                  allowClear
                  multiple
                  maxTagCount="responsive"
                  treeCheckable
                  treeCheckStrictly
                  disabled={isEdit}
                  showSearch={false}
                />
              </Form.Item>
            )}
          </Col>
          <Col span={22}>
            {isDetail ? (
              <Form.Item label="班组负责人">
                <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8 }}>
                  {detailLeadWorkers.map(worker => (
                    <Tag key={worker.id}>{worker.name}</Tag>
                  ))}
                </div>
              </Form.Item>
            ) : (
              <Form.Item
                label="班组负责人"
                name="leadWorkerIds"
                rules={[{ required: true, message: '请选择班组负责人' }]}
                extra={
                  <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8 }}>
                    {selectedLeadWorkerIds.map(id => (
                      <Tag key={id} closable onClose={() => onCloseSelectedTag('leadWorkerIds', id)}>
                        {getWorkerDisplayName(id)}
                      </Tag>
                    ))}
                  </div>
                }
              >
                <Select
                  mode="multiple"
                  options={leadWorkerSelectOptions}
                  onChange={onLeadWorkerChange}
                  tagRender={renderWorkerTag}
                  placeholder="请选择"
                  maxTagCount="responsive"
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
          </Col>
          <Col span={22}>
            {isDetail ? (
              <Form.Item label="班组人员">
                <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8 }}>
                  {detailWorkers.map(worker => (
                    <Tag key={worker.id}>{worker.name}</Tag>
                  ))}
                </div>
              </Form.Item>
            ) : (
              <Form.Item
                label="班组人员"
                name="workerIds"
                rules={[{ required: true, message: '请选择班组人员' }]}
                extra={
                  <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 4 }}>
                    {selectedWorkerIds.map(id => (
                      <Tag key={id} closable onClose={() => onCloseSelectedTag('workerIds', id)}>
                        {getWorkerDisplayName(id)}
                      </Tag>
                    ))}
                  </div>
                }
              >
                <Select
                  mode="multiple"
                  options={workerSelectOptions}
                  onChange={onWorkerChange}
                  tagRender={renderWorkerTag}
                  placeholder="请选择"
                  maxTagCount="responsive"
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
          </Col>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateTeamModal;
